<script setup lang="ts">
import type { Placement, TriggerEvent } from "floating-vue";
import type { LazyPopperComponent, PopperTriggers } from "../Popper/lazyPopper";
import { PopperContent } from "floating-vue";
import { computed, nextTick, ref, useAttrs } from "vue";
import {
  activationEventShowsPopper,
  lazyPopperComponent,
  popperThemeClasses,
  resolveShowEvents,
  useLazyPopperActivation,
} from "../Popper/lazyPopper";

defineOptions({
  name: "VTooltip",
  inheritAttrs: false,
});
const {
  disabled = false,
  content = "Content prop",
  placement = "auto",
  delay = undefined,
  align = "center",
  theme = "verbleif-tooltip-solid",
  showGroup = undefined,
  popperClass = undefined,
  distance = undefined,
  skidding = undefined,
  triggers = undefined,
} = defineProps<Props>();
const attrs = useAttrs();
const LazyPopper = lazyPopperComponent();
interface Props {
  content?: string
  align?: "left" | "center" | "right" | "justify" | "start" | "end"
  theme?: "verbleif-tooltip-gray" | "verbleif-tooltip" | "verbleif-tooltip-ac" | "verbleif-tooltip-dark" | "verbleif-tooltip-solid"
  disabled?: boolean
  delay?: string | number | {
    show: number
    hide: number
  }
  distance?: number
  skidding?: number
  placement?: Placement
  showGroup?: string
  triggers?: TriggerEvent[]
  popperClass?: string
}

// The floating-vue popper (positioning, listeners, teleported content) is only created on the first
// interaction with the reference. Overviews render thousands of tooltips over time and almost none of them
// are ever hovered; mounting them eagerly dominated the cost of scrolling and switching lists.
const rootEl = ref<HTMLElement | null>(null);
const popperRef = ref<InstanceType<LazyPopperComponent> | null>(null);
const popperContentRef = ref<InstanceType<typeof PopperContent> | null>(null);
const active = ref(false);
const isShown = ref(false);

const themeClasses = computed(() => popperThemeClasses(theme));

function getReferenceNode(): HTMLElement | null {
  return rootEl.value;
}

function getPopperNode(): Element | undefined {
  return popperContentRef.value?.$el;
}

function getTargetNodes(): Element[] {
  const root = rootEl.value;
  if (!root) {
    return [];
  }
  const popperNode = getPopperNode();
  return Array.from(root.children).filter(node => node !== popperNode);
}

async function activate(event: Event): Promise<void> {
  if (active.value || disabled) {
    return;
  }
  active.value = true;
  await nextTick();

  const popper = popperRef.value;
  if (!popper) {
    return;
  }
  const showEvents = resolveShowEvents(popper.triggers as TriggerEvent[], popper.showTriggers as PopperTriggers);
  if (!activationEventShowsPopper(event, getTargetNodes(), showEvents)) {
    return;
  }
  (event as Event & { usedByTooltip?: boolean }).usedByTooltip = true;
  popper.show({ event });
}

useLazyPopperActivation(rootEl, active, activate);
</script>

<template>
  <div
    ref="rootEl"
    class="v-popper"
    :class="[themeClasses, { 'v-popper--shown': isShown }, attrs.class]"
  >
    <slot name="reference" />
    <LazyPopper
      v-if="active"
      ref="popperRef"
      :theme="theme"
      :placement="placement"
      :disabled="disabled"
      :delay="delay"
      :triggers="triggers"
      :show-group="showGroup"
      :popper-class="popperClass"
      :distance="distance"
      :skidding="skidding"
      :target-nodes="getTargetNodes"
      :reference-node="getReferenceNode"
      :popper-node="getPopperNode"
      @apply-show="isShown = true"
      @apply-hide="isShown = false"
    >
      <template #default="slotData">
        <PopperContent
          ref="popperContentRef"
          :popper-id="slotData.popperId"
          :theme="theme"
          :shown="slotData.isShown"
          :mounted="slotData.shouldMountContent"
          :skip-transition="slotData.skipTransition"
          :auto-hide="slotData.autoHide"
          :handle-resize="slotData.handleResize"
          :classes="slotData.classes"
          :result="slotData.result"
          @hide="slotData.hide"
          @resize="slotData.onResize"
        >
          <div :class="[`text-${align}`]">
            <slot name="content">
              {{ content }}
            </slot>
          </div>
        </PopperContent>
      </template>
    </LazyPopper>
  </div>
</template>
